/**
 * Service de diagnostic pour vérifier l'accès à Excel
 * Aide à identifier les problèmes d'accès au contexte Excel
 * 
 * Vérifie:
 * 1. Disponibilité d'Excel
 * 2. Mode du document (ReadOnly vs ReadWrite)
 * 3. État readOnly du workbook
 * 4. Accès au workbook et à la feuille
 * 5. Capacité de lecture et d'écriture
 */

/* global Excel, Office */

export interface DiagnosticResult {
  excelAvailable: boolean;
  documentMode: string | null;
  workbookReadOnly: boolean | null;
  canAccessWorkbook: boolean;
  canAccessWorksheet: boolean;
  canReadData: boolean;
  canWriteData: boolean;
  errors: string[];
  warnings: string[];
  summary: string;
}

/* global Office */

export async function diagnoseExcelAccess(): Promise<DiagnosticResult> {
  const errors: string[] = [];
  const warnings: string[] = [];
  let excelAvailable = false;
  let documentMode: string | null = null;
  let workbookReadOnly: boolean | null = null;
  let canAccessWorkbook = false;
  let canAccessWorksheet = false;
  let canReadData = false;
  let canWriteData = false;

  try {
    // 1. Vérifier que Excel est disponible
    if (typeof Excel === 'undefined') {
      errors.push('Excel object not available');
      return {
        excelAvailable,
        documentMode,
        workbookReadOnly,
        canAccessWorkbook,
        canAccessWorksheet,
        canReadData,
        canWriteData,
        errors,
        warnings,
        summary: '❌ Excel not available',
      };
    }
    excelAvailable = true;
    console.log('✅ Excel object available');

    // 2. Vérifier le mode du document via Office.context
    if (typeof Office !== 'undefined' && Office.context && Office.context.document) {
      const mode = Office.context.document.mode;
      documentMode = mode ? String(mode) : null;
      console.log(`📋 Document mode: ${documentMode}`);

      if (documentMode === 'ReadOnly' || mode === 0) {
        errors.push('Document is in ReadOnly mode - cannot write');
        console.error('❌ Document is in ReadOnly mode');
      }
    } else {
      warnings.push('Office.context not available for mode check');
    }

    // 3. Essayer d'accéder au workbook et vérifier readOnly
    await Excel.run(async (context) => {
      try {
        console.log('🔍 Diagnostic: Vérification de l\'accès à Excel...');

        // Vérifier l'accès au workbook
        const workbook = context.workbook;
        if (!workbook) {
          errors.push('Cannot access workbook');
          return;
        }
        canAccessWorkbook = true;
        console.log('✅ Workbook accessible');

        // Charger la propriété readOnly du workbook
        workbook.load('readOnly');
        await context.sync();

        workbookReadOnly = workbook.readOnly;
        console.log(`📋 Workbook readOnly property: ${workbookReadOnly}`);

        if (workbookReadOnly) {
          errors.push('Workbook is marked as readOnly - cannot write');
          console.error('❌ Workbook is readOnly');
        }

        // Vérifier l'accès à la feuille active
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        if (!sheet) {
          errors.push('Cannot access active worksheet');
          return;
        }
        canAccessWorksheet = true;
        console.log('✅ Worksheet accessible');

        // Essayer de lire des données
        const usedRange = sheet.getUsedRange();
        usedRange.load('values');
        await context.sync();
        if (usedRange.values && usedRange.values.length > 0) {
          canReadData = true;
          console.log(`✅ Can read data (${usedRange.values.length} rows)`);
        } else {
          console.log('⚠️  No data to read (empty worksheet)');
        }

        // Essayer d'écrire une donnée de test
        try {
          const testCell = sheet.getRange('A1');
          testCell.load('values');
          await context.sync();
          const originalValue = testCell.values;

          // Écrire une valeur de test
          testCell.values = [['TEST_WRITE_' + Date.now()]];
          await context.sync();

          // Restaurer la valeur originale
          testCell.values = originalValue;
          await context.sync();

          canWriteData = true;
          console.log('✅ Can write data');
        } catch (writeError) {
          const writeMsg = writeError instanceof Error ? writeError.message : 'Unknown error';
          errors.push(`Cannot write data: ${writeMsg}`);
          console.error('❌ Write test failed:', writeError);
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        errors.push(`Excel.run error: ${errorMsg}`);
        console.error('❌ Excel.run error:', error);
      }
    });
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    errors.push(`Diagnostic error: ${errorMsg}`);
    console.error('❌ Diagnostic error:', error);
  }

  // Générer un résumé
  let summary = '';
  if (errors.length > 0) {
    summary = `❌ ${errors.length} error(s) found`;
  } else if (warnings.length > 0) {
    summary = `⚠️  ${warnings.length} warning(s) - but should work`;
  } else if (canWriteData) {
    summary = '✅ All checks passed - write access available';
  } else {
    summary = '⚠️  Some checks failed';
  }

  return {
    excelAvailable,
    documentMode,
    workbookReadOnly,
    canAccessWorkbook,
    canAccessWorksheet,
    canReadData,
    canWriteData,
    errors,
    warnings,
    summary,
  };
}

/**
 * Affiche un rapport de diagnostic dans la console
 */
export async function printDiagnosticReport(): Promise<void> {
  console.log('🔍 === DIAGNOSTIC EXCEL ===');
  const result = await diagnoseExcelAccess();

  console.log('📊 Résultats:');
  console.log(`  Excel disponible: ${result.excelAvailable ? '✅' : '❌'}`);
  console.log(`  Accès au workbook: ${result.canAccessWorkbook ? '✅' : '❌'}`);
  console.log(`  Accès à la feuille: ${result.canAccessWorksheet ? '✅' : '❌'}`);
  console.log(`  Lecture de données: ${result.canReadData ? '✅' : '❌'}`);
  console.log(`  Écriture de données: ${result.canWriteData ? '✅' : '❌'}`);

  if (result.errors.length > 0) {
    console.log('❌ Erreurs:');
    result.errors.forEach((error) => console.log(`  - ${error}`));
  }

  console.log('🔍 === FIN DIAGNOSTIC ===');
}
